import { useLocation, Link } from 'react-router-dom';
import { Heart, MapPin, Users, Shield, ArrowRight, ChevronRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEOHead from '../components/SEOHead';
import SEO_PAGES from '../data/seoPages';

export default function SEOLandingPage() {
  const location = useLocation();
  const slug = location.pathname.replace('/', '');
  const page = SEO_PAGES[slug];

  if (!page) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-serif text-[#2D2D2A] mb-4">Page not found</h1>
          <Link to="/" className="text-[#6E1A24] hover:underline">Back to homepage</Link>
        </div>
      </div>
    );
  }

  const otherPages = Object.values(SEO_PAGES)
    .filter(p => p.slug !== slug)
    .slice(0, 4);

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid={`seo-page-${slug}`}>
      <SEOHead
        title={page.title}
        description={page.metaDescription}
        path={`/${slug}`}
      />

      {/* Header */}
      <header className="fixed top-0 w-full z-50 backdrop-blur-xl bg-[#FAF9F6]/90 border-b border-[#E5E3DB]/50">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <Heart className="w-9 h-9 text-[#6E1A24]" strokeWidth={1.5} fill="currentColor" fillOpacity={0.1} />
            <span className="text-2xl font-serif font-medium text-[#2D2D2A] tracking-tight">LusoLove</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login" className="text-sm font-medium text-[#6C6C67] hover:text-[#6E1A24] transition-colors hidden sm:block">Sign In</Link>
            <Link to="/register">
              <Button className="bg-[#6E1A24] hover:bg-[#8A2331] text-white rounded-full px-6 py-2 text-sm font-semibold" data-testid="seo-cta-header">
                Join Free
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-16 px-6 sm:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#6E1A24]/8 text-[#6E1A24] rounded-full px-5 py-2 text-sm font-medium mb-8">
            <span>{page.ptFlag}</span>
            <span>{page.flag}</span>
            <span>{page.communitySize} Portuguese in {page.country}</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-medium text-[#2D2D2A] leading-tight mb-6" data-testid="seo-h1">
            {page.h1}
          </h1>

          <p className="text-lg sm:text-xl text-[#6C6C67] max-w-2xl mx-auto mb-10 leading-relaxed">
            {page.heroSubtitle}. LusoLove is the dating platform made exclusively for Portuguese people living abroad.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button className="bg-[#6E1A24] hover:bg-[#8A2331] text-white rounded-full px-8 py-3.5 text-base font-semibold flex items-center gap-2 w-full sm:w-auto" data-testid="seo-cta-hero">
                Create Your Profile <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/#how">
              <Button variant="outline" className="rounded-full px-8 py-3.5 text-base border-[#E5E3DB] text-[#2D2D2A] hover:bg-[#F5F2EB] w-full sm:w-auto">
                How It Works
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="py-8 border-y border-[#E5E3DB]">
        <div className="max-w-4xl mx-auto px-6 flex flex-wrap justify-center gap-12 sm:gap-20">
          <div className="text-center">
            <p className="text-2xl sm:text-3xl font-serif font-medium text-[#6E1A24]">{page.stats.members}</p>
            <p className="text-sm text-[#6C6C67] mt-1">Members</p>
          </div>
          <div className="text-center">
            <p className="text-2xl sm:text-3xl font-serif font-medium text-[#6E1A24]">{page.stats.matches}</p>
            <p className="text-sm text-[#6C6C67] mt-1">Matches Made</p>
          </div>
          <div className="text-center">
            <p className="text-2xl sm:text-3xl font-serif font-medium text-[#6E1A24]">{page.stats.cities}</p>
            <p className="text-sm text-[#6C6C67] mt-1">Cities Covered</p>
          </div>
        </div>
      </section>

      {/* Content sections */}
      <section className="py-16 px-6 sm:px-12">
        <div className="max-w-3xl mx-auto space-y-14">
          {page.content.map((section, i) => (
            <article key={section.heading}>
              <h2 className="text-xl sm:text-2xl font-serif font-medium text-[#2D2D2A] mb-4">{section.heading}</h2>
              <p className="text-[#6C6C67] leading-relaxed text-base">{section.text}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Cities covered */}
      <section className="py-12 bg-white border-y border-[#E5E3DB]">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-xl font-serif font-medium text-[#2D2D2A] mb-6 text-center">
            Portuguese singles in these cities
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            {page.cities.map(city => (
              <span key={city} className="inline-flex items-center gap-1.5 bg-[#F5F2EB] text-[#2D2D2A] rounded-full px-4 py-2 text-sm font-medium">
                <MapPin className="w-3.5 h-3.5 text-[#6E1A24]" />
                {city}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Why LusoLove */}
      <section className="py-16 px-6 sm:px-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-serif font-medium text-[#2D2D2A] mb-10 text-center">
            Why Portuguese singles choose LusoLove
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { icon: Shield, title: 'Verified Profiles', desc: 'Every member is verified with a selfie. No bots, no fakes, no catfish.' },
              { icon: Users, title: 'Portuguese Only', desc: 'A community built exclusively for the Portuguese diaspora in Europe.' },
              { icon: Heart, title: 'Real Intentions', desc: 'Members state their relationship goals upfront. No guessing games.' },
            ].map(item => (
              <div key={item.title} className="bg-white rounded-2xl border border-[#E5E3DB] p-6 text-center">
                <div className="w-12 h-12 rounded-xl bg-[#6E1A24]/8 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-6 h-6 text-[#6E1A24]" />
                </div>
                <h3 className="font-medium text-[#2D2D2A] mb-2">{item.title}</h3>
                <p className="text-sm text-[#6C6C67] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 sm:px-12 bg-[#2D2D2A]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-serif font-medium text-white mb-4">
            Ready to find your Portuguese match in {page.country}?
          </h2>
          <p className="text-[#9C9C97] mb-8 leading-relaxed">
            Join thousands of Portuguese singles already on LusoLove. Create your free profile in under 2 minutes.
          </p>
          <Link to="/register">
            <Button className="bg-[#6E1A24] hover:bg-[#8A2331] text-white rounded-full px-10 py-4 text-base font-semibold" data-testid="seo-cta-bottom">
              Join LusoLove Free <ArrowRight className="w-5 h-5 ml-2 inline" />
            </Button>
          </Link>
          <div className="flex flex-wrap justify-center gap-6 mt-8 text-sm text-[#9C9C97]">
            <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#2D6A4F]" /> Free to join</span>
            <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#2D6A4F]" /> Verified profiles</span>
            <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#2D6A4F]" /> No bots</span>
          </div>
        </div>
      </section>

      {/* Related pages (internal linking) */}
      <section className="py-12 px-6 sm:px-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-lg font-serif font-medium text-[#2D2D2A] mb-6 text-center">
            Explore more Portuguese communities
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {otherPages.map(p => (
              <Link
                key={p.slug}
                to={`/${p.slug}`}
                className="flex items-center gap-2 bg-white rounded-xl border border-[#E5E3DB] px-4 py-3 hover:border-[#6E1A24]/30 hover:shadow-sm transition-all group"
                data-testid={`seo-link-${p.slug}`}
              >
                <span>{p.flag}</span>
                <span className="text-sm font-medium text-[#2D2D2A] flex-1 truncate">{p.title.replace('Portuguese Dating in ', '')}</span>
                <ChevronRight className="w-4 h-4 text-[#9C9C97] group-hover:text-[#6E1A24] transition-colors" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-6 border-t border-[#E5E3DB] bg-white">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-[#6E1A24]" strokeWidth={1.5} fill="currentColor" fillOpacity={0.1} />
            <span className="font-serif text-lg text-[#2D2D2A]">LusoLove</span>
          </div>
          <div className="flex gap-6 text-sm text-[#6C6C67]">
            <Link to="/privacy" className="hover:text-[#6E1A24]">Privacy</Link>
            <Link to="/terms" className="hover:text-[#6E1A24]">Terms</Link>
            <Link to="/contact" className="hover:text-[#6E1A24]">Contact</Link>
            <Link to="/faq" className="hover:text-[#6E1A24]">FAQ</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
